/**
 * Canonical enum schemas mirroring DB columns and domain constants.
 *
 * Source of truth: `sql/001_init_master_schema.sql` and
 * `layer/nodejs/node_modules/leviathan-shared/{rbac-helper,validators,datamodel-templates}.js`.
 */
import { z } from 'zod';
// ============================================================================
// Auth & RBAC
// ============================================================================
/**
 * Role visible at the API level.
 *
 * DB representation: `is_super_admin BOOLEAN` + `role ENUM('owner','analyst','viewer')`.
 * When `is_super_admin = true`, `role` is NULL and the API-level role is `super_admin`.
 */
export const Role = z.enum(['super_admin', 'owner', 'analyst', 'viewer']);
/** Role assignable on invitations. Excludes `super_admin` (created out-of-band). */
export const InviteableRole = z.enum(['owner', 'analyst', 'viewer']);
// ============================================================================
// Tenants
// ============================================================================
export const PlanTier = z.enum(['starter', 'professional', 'business', 'enterprise']);
export const TenantStatus = z.enum(['trial', 'active', 'suspended', 'cancelled']);
// ============================================================================
// Connectors
// ============================================================================
/** Matches `connectors.type` ENUM. Includes `shopify` (present in DDL). */
export const ConnectorType = z.enum([
    'mysql',
    'postgresql',
    'woocommerce',
    'prestashop',
    'shopify',
    'rest_api',
    'csv',
]);
export const ConnectorStatus = z.enum(['active', 'paused', 'error', 'testing']);
// ============================================================================
// Jobs & Job Runs
// ============================================================================
export const JobStatus = z.enum(['active', 'paused', 'error']);
export const JobRunStatus = z.enum(['running', 'success', 'failed', 'cancelled']);
export const JobTriggerType = z.enum(['manual', 'scheduled', 'api']);
// ============================================================================
// Uploads
// ============================================================================
export const UploadStatus = z.enum([
    'pending',
    'confirmed',
    'processing',
    'completed',
    'failed',
]);
// ============================================================================
// Invitations
// ============================================================================
export const InvitationStatus = z.enum(['pending', 'accepted', 'expired', 'cancelled']);
// ============================================================================
// Verticals / DataModel templates
// ============================================================================
/**
 * Vertical / DataModel template identifier.
 *
 * Must match keys registered in `layer/.../datamodel-templates.js`. When adding
 * a new template, add the ID here and the corresponding template definition in
 * `@leviathan/shared`.
 */
export const Vertical = z.enum([
    'ecommerce-minimal',
    'ecommerce-full',
    'sales-agent',
    'saas',
    'restaurant',
]);
// ============================================================================
// Formatting hints (used by DataModel measures and KPIs)
// ============================================================================
export const ValueFormat = z.enum(['currency', 'percentage', 'number', 'date', 'string']);
export const TrendDirection = z.enum(['up', 'down', 'flat']);
export const Granularity = z.enum(['day', 'week', 'month', 'quarter', 'year']);
export const SortDirection = z.enum(['asc', 'desc']);
//# sourceMappingURL=index.js.map